import { AlertTriangle, FileText, House, RefreshCw } from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { ThreadSkeleton, ThreadSkeletonItem } from "@/components/agent/ThreadSkeleton";
import { ScrollArea, ScrollBar } from "@/components/atomic/ScrollArea";
import { routes } from "@/configs/router.config/routes";
import { PAGE_SIZE, useInfiniteThreads } from "@/hooks/agent/useInfiniteThreads";
import { cn } from "@/lib/utils";
import { Thread } from "@/models/agent/thread";
import { useAppStore } from "@/stores/ui/useAppStore";
import { Button } from "./Button";

const LinkButton: React.FC<{
  icon: React.ReactNode;
  text: string;
  isSideBarOpen: boolean;
  onClick: () => void;
}> = ({ icon, text, isSideBarOpen, onClick }) => {
  return (
    <div className="flex-shrink-0 px-[10px]">
      <button
        onClick={onClick}
        className={cn(
          "flex items-center space-x-3 pl-3 h-10 rounded-[10px] transition-colors hover:bg-[#5D77BE] cursor-pointer",
          isSideBarOpen ? "w-full" : "",
        )}
      >
        {icon}
        <span
          className={cn(
            "leading-[1.0] text-sm font-medium transition-all duration-200 ease-in-out overflow-hidden",
            isSideBarOpen ? "opacity-100" : "opacity-0 w-0",
          )}
        >
          {text}
        </span>
      </button>
    </div>
  );
};

const ThreadItem: React.FC<{
  thread: Thread;
  isSelected: boolean;
  isSideBarOpen: boolean;
  onClick: () => void;
}> = ({ thread, isSelected, isSideBarOpen, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        "h-9 mb-[1px] rounded-[10px] cursor-pointer transition-all duration-200 ease-in-out font-medium text-left",
        "block",
        isSelected
          ? "bg-[#5D77BE]"
          : "hover:bg-[#5D77BE]",
        isSideBarOpen ? "w-full" : "w-0 bg-transparent",
      )}
    >
      <div
        className={cn(
          "px-2 leading-9 truncate w-full transition-all duration-200 ease-in-out",
          isSideBarOpen ? "opacity-100" : "opacity-0 w-0",
        )}
        title={thread.title}
      >
        {thread.displayTitle}
      </div>
    </button>
  );
};

const ErrorState: React.FC<{ onRetry: () => Promise<void>; }> = ({ onRetry }) => {
  return (
    <div className="flex flex-col items-center justify-center text-center px-4">
      <div className="flex flex-col items-center justify-center py-8 space-y-4">
        {/* Error Icon */}
        <div className="w-10 h-10 bg-gray-200/30 rounded-full flex items-center justify-center">
          <AlertTriangle className="w-5 h-5 text-white" />
        </div>

        {/* Error Message */}
        <div className="text-center space-y-2">
          <h4 className="text-sm">読み込みエラー</h4>
          <p className="text-xs px-2 leading-relaxed">
            スレッドの取得に失敗しました。
          </p>
        </div>

        {/* Retry Button */}
        <Button
          onClick={async () => {
            await onRetry();
          }}
          variant="outline"
          size="sm"
          className="gap-2 h-8 text-xs px-3"
        >
          <RefreshCw className={`w-3 h-3`} />
          再試行
        </Button>
      </div>
    </div>
  );
};

export const ThreadList: React.FC = () => {
  const { workspaceId, threadId: selectedThreadId } = useParams();
  const navigate = useNavigate();
  const setIsAgentMode = useAppStore(s => s.setIsAgentMode);
  const [isSideBarOpen] = useLocalStorage("isSideBarOpen", true);

  const {
    threads,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    refresh,
    observerRef,
  } = useInfiniteThreads();

  const onNewChat = () => {
    if (workspaceId) {
      navigate(routes.agent(workspaceId));
    }
  };

  const onThreadSelect = (thread: Thread) => {
    if (workspaceId) {
      navigate(routes.agentThread(workspaceId)(thread.id));
    }
  };

  const renderContent = () => {
    // 初回ローディング中
    if (isLoading && isSideBarOpen) {
      return isSideBarOpen
        ? (
          <div className="max-w-full px-1.5">
            <div className="h-9 leading-9 px-2 font-bold cursor-default text-[#A9B5D6]">履歴</div>
            <ThreadSkeleton count={20} />
          </div>
        )
        : null;
    }

    // エラー状態
    if (error) {
      return isSideBarOpen ? <ErrorState onRetry={refresh} /> : null;
    }

    // 通常状態
    return (
      <>
        {/* スレッド一覧 */}
        <ScrollArea className="flex-1">
          <div className="max-w-[258px] px-1.5 pb-1">
            <div
              className={cn(
                "h-9 leading-9 px-2 font-bold cursor-default text-xs text-[#A9B5D6]",
                "transition-all duration-200 ease-in-out",
                isSideBarOpen ? "opacity-100" : "opacity-0",
              )}
            >
              履歴
            </div>
            <div>
              {threads.map((thread) => (
                <ThreadItem
                  key={thread.id}
                  thread={thread}
                  isSelected={selectedThreadId === thread.id}
                  isSideBarOpen={isSideBarOpen}
                  onClick={() => onThreadSelect(thread)}
                />
              ))}

              {/* 追加ロード用のスケルトン */}
              {isLoadingMore && (
                <div className="mt-2">
                  {Array.from({ length: PAGE_SIZE }).map((_, index) => <ThreadSkeletonItem key={index} />)}
                </div>
              )}

              {/* 無限スクロール用のトリガー要素 */}
              {hasMore && !isLoadingMore && threads.length > 0 && (
                <div
                  ref={observerRef}
                  className="h-4 bg-transparent flex items-center justify-center"
                  style={{ minHeight: "16px" }}
                >
                  <div className="w-2 h-2 bg-gray-200 rounded-full opacity-20" />
                </div>
              )}
            </div>
          </div>
          <ScrollBar orientation="vertical" barColor="#ffffff80" />
        </ScrollArea>
      </>
    );
  };

  return (
    <div className="h-[calc(100vh-100px)] w-[260px] ml-[-10px] flex flex-col gap-4 overflow-hidden text-white">
      <div className="flex flex-col gap-1">
        <LinkButton
          icon={<House className="h-4 w-4" strokeWidth={2} />}
          text="ホーム"
          isSideBarOpen={isSideBarOpen}
          onClick={() => {
            setIsAgentMode(false);
            navigate(routes.top(workspaceId || ""));
          }}
        />
        <LinkButton
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          }
          isSideBarOpen={isSideBarOpen}
          text="新しいチャット"
          onClick={onNewChat}
        />
        <LinkButton
          icon={<FileText className="h-4 w-4" strokeWidth={2} />}
          text="プロンプト管理"
          isSideBarOpen={isSideBarOpen}
          onClick={() => navigate(routes.agentManagement(workspaceId || ""))}
        />
      </div>
      {renderContent()}
    </div>
  );
};
